import { Image, Product } from '../models/products';

function pick(u: string | undefined): string | undefined {
    return u && u.trim() !== '' ? u : undefined;
}

/** Best Rezdy-backed URL for a single gallery image (itemUrl first). */
function bestUrlFromPhoto(photo: Image): string | undefined {
    return pick(photo.url) ?? pick(photo.largeURL) ?? pick(photo.mediumURL) ?? pick(photo.thumbnailURL);
}

/** Rezdy / merged `photos[]` only — used when `product.heroImage` is absent or incomplete. */
export function rezdyHeroPhotoUrl(product: Product | undefined): string | undefined {
    if (!product?.photos?.length) return undefined;
    const withItemUrl = product.photos.find((p) => p.url && p.url.trim() !== '');
    if (withItemUrl) return bestUrlFromPhoto(withItemUrl);
    return bestUrlFromPhoto(product.photos[0]);
}

export type ResolvedHeroImage = {
    /** When set, render `<picture><source type="image/webp" />…` */
    readonly webp?: string;
    /** `<img src>` — self-hosted JPEG and/or Rezdy fallback */
    readonly imgSrc: string;
};

/**
 * Prefer `product.heroImage` (webp + jpeg from your CDN), then Rezdy photos.
 * `<img>` uses jpeg when provided, otherwise Rezdy URL (so WebP-capable browsers still get `source`).
 */
export function resolveHeroImage(product: Product | undefined): ResolvedHeroImage | null {
    const rezdy = rezdyHeroPhotoUrl(product);
    const hi = product?.heroImage;
    const webp = pick(hi?.webp);
    const jpeg = pick(hi?.jpeg);

    if (webp || jpeg) {
        const imgSrc = jpeg || rezdy || webp;
        if (!imgSrc) return null;
        return webp ? { webp, imgSrc } : { imgSrc };
    }

    if (rezdy) return { imgSrc: rezdy };
    return null;
}
